import { motion } from "framer-motion";
import { ShieldCheck, ArrowRight } from "lucide-react";
import BrandName from "@/components/site/BrandName";
import { artwork } from "@/lib/brand-artwork";

const answers = [
  "Every policy is signed.",
  "Every staff member is trained.",
  "Every certificate is on file.",
  "Every legislative update is recorded.",
  "Every audit event is chained.",
];

export default function InspectorMoment() {
  return (
    <section
      id="inspector"
      data-testid="inspector-section"
      className="relative py-24 md:py-32 border-t border-white/[0.04] overflow-hidden"
    >
      {/* Subtle radial spot */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0A1220]/60 to-transparent" />
        <div className="absolute top-1/2 left-1/4 -translate-x-1/2 -translate-y-1/2 h-[520px] w-[520px] rounded-full bg-[#D4A65A]/[0.05] blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left — typography moment */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6 }}
            >
              <div className="eyebrow">The moment that matters</div>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.75, delay: 0.05 }}
              className="mt-6 font-display text-[38px] sm:text-5xl md:text-[64px] tracking-[-0.04em] text-white leading-[1] font-semibold"
            >
              What happens when
              <br />
              <span className="text-[#D4A65A]">an inspector arrives?</span>
            </motion.h2>

            <motion.ul
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-10 md:mt-12 flex flex-col gap-3"
            >
              {answers.map((line, i) => (
                <motion.li
                  key={line}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.45, delay: 0.4 + i * 0.1 }}
                  className="flex items-center gap-3 text-white text-[17px] md:text-[19px] tracking-tight font-medium"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-[#D4A65A] shrink-0" />
                  {line}
                </motion.li>
              ))}
            </motion.ul>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: 1.05 }}
              className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4"
            >
              <div className="inline-flex items-center gap-2 text-emerald-300 text-[11px] uppercase tracking-[0.24em] font-semibold">
                <ShieldCheck size={13} />
                Already inspection ready
              </div>
              <span className="hidden sm:block text-slate-600">·</span>
              <a
                href="#tryit"
                data-testid="inspector-cta"
                className="inline-flex items-center gap-2 text-[13px] font-semibold text-white/90 hover:text-white transition-colors group"
              >
                See how <BrandName /> makes this the reality
                <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
              </a>
            </motion.div>
          </div>

          {/* Right — brand social artwork, subtly framed */}
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.75, delay: 0.1 }}
            className="lg:col-span-5"
          >
            <div
              data-testid="inspector-artwork"
              className="relative rounded-2xl overflow-hidden border border-white/[0.08] bg-[#050B18] shadow-[0_40px_120px_-40px_rgba(0,0,0,0.9)]"
            >
              {/* Ambient glows behind the image */}
              <div className="pointer-events-none absolute -top-16 -left-16 h-56 w-56 rounded-full bg-emerald-500/[0.12] blur-3xl" />
              <div className="pointer-events-none absolute -bottom-16 -right-16 h-56 w-56 rounded-full bg-[#D4A65A]/[0.1] blur-3xl" />

              {/* Poster chrome — tiny header bar so it reads like a piece of brand collateral, not a photo */}
              <div className="relative flex items-center justify-between px-4 py-2.5 border-b border-white/[0.06] bg-black/40 backdrop-blur">
                <div className="inline-flex items-center gap-2 text-[9.5px] uppercase tracking-[0.24em] text-[#D4A65A] font-semibold">
                  <span className="h-1 w-3 bg-[#D4A65A]/80" />
                  Brand · Poster
                </div>
                <span className="text-[9.5px] font-mono text-slate-500">
                  cannicomply / social
                </span>
              </div>

              <img
                src={artwork.proveIt}
                alt="CanniComply poster: Can you prove it? If an inspector asked today... could you?"
                loading="lazy"
                decoding="async"
                className="relative w-full h-auto block"
              />

              {/* Bottom caption strip */}
              <div className="relative px-4 py-3 border-t border-white/[0.06] bg-black/40 backdrop-blur flex items-center justify-between gap-3">
                <div className="text-[11px] text-slate-400 leading-snug truncate">
                  If an inspector asked today, could you prove it?
                </div>
                <span className="shrink-0 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-semibold">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  Yes
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
